import React from "react";
import { Routes, Route } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { usePlenumAccess } from "../hooks/use-plenum-access";
import { useIsAskAdmin } from "../hooks/use-is-ask-admin";

const SignInPage = React.lazy(() => import("./auth/SignInPage"));
const SignUpPage = React.lazy(() => import("./auth/SignUpPage"));
const AccessDenied = React.lazy(() => import("./auth/AccessDenied"));

import AppWrapper from "./base/AppWrapper";

const LoadingScreen = () => (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#5D82ED] to-[#C08AF8]">
        <div className="text-center">
            <div className="flex items-center justify-center gap-2">
                <div className="h-2 w-2 bg-white rounded-full animate-bounce [animation-delay:-0.3s]" />
                <div className="h-2 w-2 bg-white rounded-full animate-bounce [animation-delay:-0.15s]" />
                <div className="h-2 w-2 bg-white rounded-full animate-bounce" />
            </div>
        </div>
    </div>
);

function ProtectedRoute({
    children,
    isSignedIn,
    accessGranted,
}: {
    children: React.ReactNode;
    isSignedIn: boolean;
    accessGranted: boolean;
}) {
    if (!isSignedIn) {
        return (
            <React.Suspense fallback={<LoadingScreen />}>
                <SignInPage />
            </React.Suspense>
        );
    }
    if (!accessGranted) {
        return <LoadingScreen />;
    }
    return <>{children}</>;
}

const About = React.lazy(() => import("./about/About"));
const Admin = React.lazy(() => import("./admin/Admin"));
const SuperAdmin = React.lazy(() => import("./super-admin/SuperAdmin"));
const Ask = React.lazy(() => import("./ask/Ask"));
const Contact = React.lazy(() => import("./contact/Contact"));
const DocumentLibrary = React.lazy(
    () => import("./document-library/DocumentLibrary"),
);
const FileManager = React.lazy(() => import("./files/FileManager"));
const EnterPayment = React.lazy(() => import("./EnterPayment"));
const Homepage = React.lazy(() => import("./homepage/Homepage"));
const Legal = React.lazy(() => import("./Legal"));
const LogOut = React.lazy(() => import("./LogOut"));
const PaymentSuccess = React.lazy(() => import("./PaymentSuccess"));
const Pricing = React.lazy(() => import("./pricing/Pricing"));
const Settings = React.lazy(() => import("./settings/Settings"));

const AdminPage = AppWrapper(Admin);
const SuperAdminPage = AppWrapper(SuperAdmin);
const AskPage = AppWrapper(Ask);
const DocumentLibraryPage = AppWrapper(DocumentLibrary);
const FilesPage = AppWrapper(FileManager);
const SettingsPage = AppWrapper(Settings);

export default function Router() {
    const { isLoaded, isSignedIn } = useAuth();
    const {
        accessDenied,
        accessGranted,
        isLoading: accessLoading,
        accessError,
        retry,
    } = usePlenumAccess();

    // Show loading while Clerk or access check is in progress
    if (!isLoaded || accessLoading) {
        return <LoadingScreen />;
    }

    // Show access denied if user is signed in but doesn't have access
    if (isSignedIn && (accessDenied || accessError)) {
        return (
            <React.Suspense fallback={<LoadingScreen />}>
                <AccessDenied onRetry={accessError ? retry : undefined} />
            </React.Suspense>
        );
    }

    return (
        <React.Suspense fallback={<LoadingScreen />}>
            <Routes>
                <Route path="/about" Component={About} />
                <Route
                    path="/ask"
                    element={
                        <ProtectedRoute isSignedIn={isSignedIn ?? false} accessGranted={accessGranted}>
                            <AskPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/document-library"
                    element={
                        <ProtectedRoute isSignedIn={isSignedIn ?? false} accessGranted={accessGranted}>
                            <DocumentLibraryPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/files"
                    element={
                        <ProtectedRoute isSignedIn={isSignedIn ?? false} accessGranted={accessGranted}>
                            <FilesPage />
                        </ProtectedRoute>
                    }
                />

                <Route path="/contact" Component={Contact} />
                <Route path="/enter-payment" Component={EnterPayment} />
                <Route path="/" Component={Homepage} />
                <Route path="/legal" Component={Legal} />
                <Route path="/logout" Component={LogOut} />

                <Route path="/sign-in/*" Component={SignInPage} />
                <Route path="/sign-up/*" Component={SignUpPage} />

                <Route path="/payment-success" Component={PaymentSuccess} />
                <Route path="/pricing" Component={Pricing} />
                <Route
                    path="/settings/*"
                    element={
                        <ProtectedRoute isSignedIn={isSignedIn ?? false} accessGranted={accessGranted}>
                            <SettingsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin"
                    element={
                        <ProtectedRoute isSignedIn={isSignedIn ?? false} accessGranted={accessGranted}>
                            <AdminPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/super-admin"
                    element={
                        <ProtectedRoute isSignedIn={isSignedIn ?? false} accessGranted={accessGranted}>
                            <SuperAdminPage />
                        </ProtectedRoute>
                    }
                />
            </Routes>
        </React.Suspense>
    );
}
